import { ProcessServerConfigFunction } from 'filepond';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { QueryObserverResult } from 'react-query';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import {
  CaseByIdQuery,
  CaseDocumentationType,
  UploadSource,
  useCompleteAuditEntryMutation,
  useCreateAuditEntryMutation,
  useCreateCaseDocumentationUploadUrlMutation,
  useCreateCaseDocumentUploadMutation,
  useGetCaseDocumentUploadUrlMutation,
} from 'src/graphql';
import { AuditActions, SaveDocumentButtonStates } from 'src/types';
import {
  getScanStatusErrorMessage,
  shouldBlockDownload,
} from 'src/utils/scanStatus';
import { uploadToPresignedUrl } from 'src/utils/uploadToPresignedUrl';

interface FileUpload {
  __typename?: 'CaseDocumentationUploadType' | undefined;
  s3path: string;
  filename?: string | null | undefined;
  scanStatus?: string | null | undefined;
  scannedAt?: string | null | undefined;
  uploadSource?: UploadSource | null | undefined;
  size?: number | null | undefined;
}
export interface UseCreateDocument {
  value: string;
  setValue: (value: string) => void;
  title: string;
  setTitle: (title: string) => void;
  documentHasChanged: boolean;
  setDocumentHasChanged: (documentHasChanged: boolean) => void;
  uploads: FileUpload[];
  setUploads: (uploads: FileUpload[]) => void;
  filepondFiles: File[];
  setFilepondFiles: (filepondFiles: File[]) => void;
  uploadFile: ProcessServerConfigFunction;
  handleOnRemoveFile: (file: FileUpload) => void;
  handleOpenFile: (file: FileUpload) => void;
}

export const useCreateDocument = (
  caseId: string,
  openDocument: CaseDocumentationType | null,
  setDocumentState: (documentState: SaveDocumentButtonStates) => void,
  refetchDocuments: () => Promise<QueryObserverResult<CaseByIdQuery, unknown>>,
): UseCreateDocument => {
  const [value, setValue] = useState<string>(openDocument?.value ?? '');
  const [title, setTitle] = useState<string>(openDocument?.title ?? '');
  const [documentHasChanged, setDocumentHasChanged] = useState(false);
  const [uploads, setUploads] = useState<
    {
      __typename?: 'CaseDocumentationUploadType' | undefined;
      s3path: string;
      filename?: string | null | undefined;
      scanStatus?: string | null | undefined;
      scannedAt?: string | null | undefined;
      uploadSource?: UploadSource | null | undefined;
    }[]
  >([]);
  const [filepondFiles, setFilepondFiles] = useState<File[]>([]);

  useEffect(() => {
    const hasChanged =
      value !== (openDocument?.value ?? '') ||
      title !== (openDocument?.title ?? '') ||
      !isEqual(openDocument?.Uploads ?? [], uploads);

    if (hasChanged) {
      setDocumentState(SaveDocumentButtonStates.UNSAVED);
    }
    setDocumentHasChanged(hasChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    value,
    title,
    uploads,
    openDocument?.value,
    openDocument?.title,
    openDocument?.Uploads,
  ]);

  useEffect(() => {
    setUploads(openDocument?.Uploads ?? []);
    setValue(openDocument?.value ?? '');
    setTitle(openDocument?.title ?? '');
  }, [openDocument]);

  const createUploadURL = useCreateCaseDocumentationUploadUrlMutation();

  const getUploadedDocumentURL = useGetCaseDocumentUploadUrlMutation();

  const createDocumentUpload = useCreateCaseDocumentUploadMutation();

  const createAuditEntry = useCreateAuditEntryMutation();
  const completeAuditEntry = useCompleteAuditEntryMutation();

  const uploadFile: ProcessServerConfigFunction = async (
    _fieldName,
    file,
    _metadata,
    load,
    error,
    progress,
    abort,
  ) => {
    const controller = new AbortController();
    try {
      const data = await createUploadURL.mutateAsync({
        type: file.type,
        filename: file.name,
        uploadSource: UploadSource.ReshubWorkspace,
        caseId,
        size: file.size,
      });
      const { url, s3path } = data.createCaseDocumentUploadURL;
      if (!url || !s3path) {
        return error('No upload URL available');
      }
      const resp = await uploadToPresignedUrl({
        file,
        url,
        tags: { uploadSource: UploadSource.ReshubWorkspace },
        onUploadProgress: (event) =>
          progress(true, event.loaded, event.total ?? 100),
        controller,
      });
      if (resp.status > 204) {
        handleErrorToast({
          toastMessage: `File '${file.name}' failed to upload. ${resp.status} - ${resp.data}`,
        });
        throw error(`File upload returned ${resp.status} - ${resp.data}`);
      }
      load(s3path);
      await createDocumentUpload.mutateAsync({
        caseId,
        s3path,
        mimeType: file.type,
        size: file.size,
        filename: file.name,
        uploadSource: UploadSource.ReshubWorkspace,
        caseDocument: openDocument?.id,
      });
      await refetchDocuments();
      handleSuccessToast({
        toastMessage: `File '${file.name}' successfully uploaded.`,
      });
    } catch (err) {
      error(err instanceof Error ? err.message : String(err));
    }
    return {
      abort: () => {
        controller.abort();
        abort();
      },
    };
  };

  const handleOnRemoveFile = (file: FileUpload) => {
    setUploads(uploads.filter((upload) => upload.s3path !== file.s3path));
  };

  const handleOpenFile = async (file: FileUpload) => {
    if (shouldBlockDownload(file.scanStatus)) {
      handleErrorToast({
        toastMessage: 'Download blocked',
        toastDescription: getScanStatusErrorMessage(file.scanStatus),
      });
      return;
    }
    let auditEntry;
    try {
      if (openDocument?.id) {
        const { createAuditEntry: auditEntryData } =
          await createAuditEntry.mutateAsync({
            auditAction: AuditActions.VIEW_CASE_DOCUMENT_ATTACHMENT,
            auditData: {
              caseId,
              caseDocument: openDocument.id,
              uploadViewed: file.filename,
              uploadPath: file.s3path,
            },
            completed: false,
          });
        auditEntry = auditEntryData;
      }
      const {
        getCaseDocumentUploadURL: { url },
      } = await getUploadedDocumentURL.mutateAsync({
        s3Path: file.s3path,
      });
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      handleErrorToast({
        toastMessage: `Failed to open file '${file.filename}'. Please try again`,
      });
    }
    if (auditEntry) {
      completeAuditEntry.mutate({ id: auditEntry.id });
    }
  };

  return {
    value,
    setValue,
    title,
    setTitle,
    documentHasChanged,
    setDocumentHasChanged,
    uploads,
    setUploads,
    filepondFiles,
    setFilepondFiles,
    uploadFile,
    handleOnRemoveFile,
    handleOpenFile,
  };
};
